<template>
  <v-container fluid>
    <v-row class="device_details" :class="!$route.params.deviceId ? '' : 'pl-3 pr-3'">
      <v-col cols="12" class="pa-0">
        <v-toolbar class="add-opacity" color="backgroundLight" :flat="true">
          <v-toolbar-title v-if="!$route.params.deviceId" class="title primaryGrey--text">
            <router-link :to="`/device/${deviceId}`">
              <v-icon class="mr-3" small color="primaryGrey">mdi-open-in-new</v-icon>Open as a Page
            </router-link>
          </v-toolbar-title>
          <v-toolbar-title v-if="$route.params.deviceId" class="title primaryGrey--text">
            <router-link :to="`/devices`">
              <v-icon class="mr-3" color="primaryGrey">mdi-keyboard-backspace</v-icon>Back
            </router-link>
          </v-toolbar-title>
          <v-spacer></v-spacer>
        </v-toolbar>
      </v-col>
    </v-row>

    <v-row class="common-card-row">
      <v-col cols="12" md="6" class="pa-0 pr-0 pr-md-3 pb-3 pb-md-0">
        <v-col class="common-card-col">
          <div class="font-weight-bold titleLight--text">
            Device - {{ deviceId || $route.params.deviceId }}
            <v-icon
              v-if="!editable"
              color="primary"
              class="ml-2 pb-1 pointer"
              title="Edit Device Details"
              @click="update_device()"
              small
              >mdi-pencil</v-icon
            >
          </div>
          <v-row v-if="device_data" no-gutters class="mt-3">
            <v-col cols="6" class="pl-4 mb-3">
              <div class="text-caption">Asset ID</div>
              <div v-if="!editable" class="font-weight-bold">
                {{ device_data.AssetID }}
              </div>
              <v-text-field
                v-else
                v-model="temp_details.AssetID"
                class="pa-0 ma-0 mr-md-16 pr-md-16 font-weight-bold"
                required
              />
            </v-col>
            <v-col cols="6" class="pl-4 mb-3">
              <div class="text-caption">
                <div v-if="!is_showing_current_poi">
                  Current POI<v-icon
                    v-if="device_data.CurrentLat && device_data.CurrentLng"
                    @click="show_current_location"
                    class="ml-1"
                    color="accent"
                    >mdi-map-marker-circle</v-icon
                  >
                </div>
                <div v-else>
                  Current POI<v-icon @click="show_device_journey" class="ml-1" color="accent"
                    >mdi-compass-outline</v-icon
                  >
                </div>
              </div>
              <div class="font-weight-bold">{{ device_data.CurrentPOI }}</div>
            </v-col>
            <v-col cols="6" class="pl-4 mb-3">
              <div class="text-caption">POI Type</div>
              <div class="font-weight-bold">{{ device_data.LocationType }}</div>
            </v-col>
            <v-col cols="6" class="pl-4 mb-3">
              <div class="text-caption">Asset Type</div>
              <div v-if="!editable" class="font-weight-bold">{{ device_data.AssetType }}</div>
              <v-select
                class="pa-0 ma-0 mr-md-16 pr-md-16 font-weight-bold"
                v-else
                v-model="temp_details.AssetType"
                dense
                :items="page_content.asset_types"
                single-line
              ></v-select>
            </v-col>
            <v-col cols="6" class="pl-4 mb-3">
              <div class="text-caption">Stay Duration</div>
              <div class="font-weight-bold">
                {{ device_data.StayDuration | timeSince }}
              </div>
            </v-col>
            <v-col cols="6" class="pl-4 mb-3">
              <div class="text-caption">Last Message Type</div>
              <div class="font-weight-bold">
                {{ device_data.LastMessageType }}
              </div>
            </v-col>
            <v-col cols="6" class="pl-4 mb-3">
              <div class="text-caption">Last Message Time</div>
              <div class="font-weight-bold">
                {{ device_data.LastMessageTime | date }}
              </div>
            </v-col>
            <v-col cols="6" class="pl-4 mb-3">
              <div class="text-caption">Battery</div>
              <div class="font-weight-bold">
                {{ device_data.BatteryStatus }}
              </div>
            </v-col>
            <v-col cols="6" class="pl-4 mb-3">
              <div class="text-caption">Device Status</div>
              <div class="font-weight-bold">{{ device_data.DeviceStatus }}</div>
            </v-col>
            <v-col cols="6" class="pl-4 mb-3">
              <div class="text-caption">Barcode</div>
              <div v-if="!editable" class="font-weight-bold">{{ device_data.Barcode }}</div>
              <v-text-field
                v-else
                v-model="temp_details.Barcode"
                class="pa-0 ma-0 mr-md-16 pr-md-16 font-weight-bold"
                required
              />
            </v-col>
            <v-col cols="9" class="pl-1 mb-3 pr-md-12">
              <div class="text-caption pl-3">Comment</div>
              <span class="px-3 font-weight-bold" v-if="!editable">{{ device_data.Comment }}</span>
              <v-textarea
                v-else
                v-model="temp_details.Comment"
                class="px-3 comment-textarea font-weight-bold"
                placeholder="Add Device Comment"
                hint="You can enter up to 500 characters."
                rows="3"
                dense
                clearable
              ></v-textarea>
              <v-row class="d-flex justify-end mt-4 mb-4" v-if="editable">
                <v-btn small color="darken-1" class="mr-4" @click="cancel_edit">
                  <v-icon left> mdi-close-circle-outline </v-icon>
                  {{ $t("common.words.cancel") }}
                </v-btn>
                <v-btn small @click="update_details()" color="primary">
                  <v-icon left> mdi-content-save-outline </v-icon>
                  {{ $t("common.words.save") }}
                </v-btn>
              </v-row>
            </v-col>
          </v-row>
        </v-col>
      </v-col>
      <v-col cols="12" md="6" class="pa-0">
        <v-col class="common-card-col">
          <MapsVT
            v-if="map_data"
            height="300px"
            :options="options"
            :items="map_data"
            :new_item="show_map_object"
            :no_info_window="!show_info_window"
            :hide_legends="show_info_window"
            msg="No Current Location"
            :center="get_center"
            :showRoute="show_info_window"
            :clusterMarkers="!show_info_window"
            @marker_clicked="marker_clicked($event)"
          />
        </v-col>
      </v-col>
    </v-row>

    <v-row class="common-card-row pb-3" v-if="journeys_list || messages_list">
      <v-col class="common-card-col">
        <div>
          <PrimaryFiltersVT
            :list_filters="page_content.tabs"
            @filter_update="update_current_tab($event)"
            :current_tab="current_tab"
            :loading.sync="is_loading"
          ></PrimaryFiltersVT>
        </div>
        <div class="primaryDark--text">
          <DataTableVT
            v-if="current_tab == 'Journeys'"
            :headers="page_content.journeys_table"
            :items_list="journeys_list"
            item_key="id"
            :no_search="no_search"
            :selected_row="selected_row"
            @filteredItems="currentItems($event)"
            @trClick="trClick($event)"
            :loading="is_loading"
          />
          <div v-if="current_tab == 'Messages'">
            <div class="d-flex justify-center align-center">
              <div>
                <v-menu offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-bind="attrs"
                      v-on="on"
                      v-model="startDate"
                      append-icon="mdi-calendar"
                      readonly
                      label="Start Date"
                    ></v-text-field>
                  </template>
                  <v-date-picker v-model="startDate"></v-date-picker>
                </v-menu>
              </div>
              <div class="pl-4">To</div>
              <div class="pl-4">
                <v-menu offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-bind="attrs"
                      v-on="on"
                      v-model="endDate"
                      append-icon="mdi-calendar"
                      readonly
                      label="End Date"
                    ></v-text-field>
                  </template>
                  <v-date-picker v-model="endDate"></v-date-picker>
                </v-menu>
              </div>
              <div class="pl-5">
                <v-btn color="primary" small dark @click="get_message_list"> Search </v-btn>
              </div>
            </div>
            <v-card flat>
              <v-card-text class="pa-0">
                <DataTableVT
                  :headers="page_content.messages_table"
                  item_key="SeqNo"
                  sort_by="MessageTime"
                  :items_list="messages_list"
                  :selected_row="selected_row"
                  :itemsPerPage="itemsPerPage"
                  :no_search="no_search"
                  @action="show_location_on_map($event)"
                  @filteredItems="currentItems($event)"
                  @trClick="trClick($event)"
                  :loading="is_loading"
                /> </v-card-text
            ></v-card>
          </div>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import DataTableVT from "../components/DataTableVT";
import PrimaryFiltersVT from "../components/PrimaryFiltersVT";
import MapsVT from "../components/MapsVT";
import { axios_get, axios_put } from "../services/vt_service";
import { tracking } from "../services/tracking";
import { content } from "../../config/devices.js";

export default {
  props: ["deviceId"],
  watch: {
    deviceId: function () {
      this.get_device_data();
    },
    device_data: function (newVal) {
      this.temp_details = {
        AssetID: newVal.AssetID,
        Barcode: newVal.Barcode,
        AssetType: newVal.AssetType,
        Comment: newVal.Comment,
      };
    },
  },
  name: "Device",
  components: {
    DataTableVT,
    PrimaryFiltersVT,
    MapsVT,
  },
  data: function () {
    return {
      editable: false,
      is_loading: true,
      startDate: null,
      endDate: null,
      flat: true,
      no_search: true,
      network_error: false,
      page_content: null,
      journeys_list: null,
      messages_list: null,
      current_tab: "Journeys",
      current_current_location: false,
      current_position: {},
      current_battery_icon: "battery-full",
      has_coordinates: false,
      chartData: [],
      device_id: "",
      show_info_window: true,
      show_map_object: null,
      filtered_list: null,
      selected_row: null,
      itemsPerPage: 25,
      chartOptions: {
        chart: {
          title: "Device Signals",
          subtitle: "Messages received (last 7 days)",
        },
      },
      previous_data: null,
      is_showing_current_poi: false,
      temp_details: null,
      updating_device_data: false,
    };
  },
  created() {
    this.page_content = content;
  },
  mounted() {
    this.get_device_data();
  },
  methods: {
    update_device() {
      this.editable = !this.editable;
    },
    update_details() {
      this.updating_device_data = true;
      let payload = {
        DeviceID: this.device_data.DeviceID,
        AssetID: this.temp_details.AssetID,
        Barcode: this.temp_details.Barcode,
        Comment: this.temp_details.Comment,
        AssetType: this.temp_details.AssetType,
      };

      this.update_device_details(payload);

      this.editable = false;
    },
    cancel_edit() {
      this.editable = false;
      this.temp_details = {
        AssetID: this.device_data.AssetID,
        Barcode: this.device_data.Barcode,
        AssetType: this.device_data.AssetType,
        Comment: this.device_data.Comment,
      };
    },
    update_current_tab(val) {
      this.current_current_location = false;
      this.current_tab = val;
    },
    show_location_on_map(obj) {
      this.show_map_object = obj[1];
    },
    currentItems(items) {
      this.filtered_list = items;
    },
    show_current_location() {
      this.current_current_location = true;
      this.previous_data = this.filtered_list;
      this.filtered_list = null;
      this.is_showing_current_poi = true;
    },
    show_device_journey() {
      this.current_current_location = false;
      this.filtered_list = this.previous_data;
      this.is_showing_current_poi = false;
      this.previous_data = null;
    },
    async update_device_details(data) {
      this.dialog = false;
      this.network_error = false;
      const { DeviceID, Comment, AssetID, AssetType, Barcode } = data;
      await axios_put(tracking.update_device_details, { DeviceID }, data);
      this.error_message = "Device Details Successfully Saved!";
      this.snackbar = true;
      this.device_data = { ...this.device_data, Comment, AssetID, AssetType, Barcode };
      this.updating_device_data = false;
    },
    async get_device_data() {
      this.is_loading = true;
      this.network_error = false;
      if (this.$route.params && this.$route.params.deviceId) {
        this.no_search = false;
        this.device_id = this.$route.params.deviceId;
        this.$store.commit("clear_search");
        this.$store.commit("update_page_title", `Device / ${this.device_id}`);
      } else {
        this.device_id = this.deviceId;
      }
      const journeys_res = await axios_get(tracking.get_journeys, { device_id: this.device_id });
      this.journeys_list = journeys_res.map((elem) => {
        elem.id = `${Math.round(Math.random() * 10000000)}`;
        return elem;
      });
      const response = await axios_get(tracking.get_messages, { device_id: this.device_id });
      this.messages_list = response;
      this.is_loading = false;
    },
    async get_message_list() {
      this.dialog = false;
      this.network_error = false;
      const response = await axios_get(
        tracking.get_messages,
        { device_id: this.device_id },
        { start_date: this.startDate, end_date: this.endDate }
      );
      this.messages_list = response;
    },
    marker_clicked: function (ev) {
      if (!this.current_current_location) {
        this.selected_row = ev[0];
      }
    },
    trClick: function (ev) {
      console.log(ev);
    },
  },
  computed: {
    location_data: function () {
      let location_details =
        this.$store.state.locations && this.device_data
          ? this.$store.state.locations.filter((elem) => {
              if (elem.FullName == this.device_data.CurrentPOI) return elem;
            })[0]
          : null;
      return location_details ? location_details : null;
    },
    map_data: function () {
      if (this.filtered_list) {
        if (this.current_tab == "Journeys")
          return this.filtered_list.filter((elem) => {
            return elem.LastLat && elem.LastLng;
          });
        else
          return this.filtered_list.filter((elem, indx) => {
            return (
              (!(indx != 0 && elem.FullName == this.filtered_list[indx - 1].FullName) &&
                elem.Latitude &&
                elem.Longitude) ||
              (indx == 0 && elem.Latitude && elem.Longitude)
            );
          });
      }
      return this.messages_list && this.messages_list[0] ? [this.messages_list[0]] : [];
    },
    get_center: function () {
      return this.map_data && this.location_data && this.current_current_location
        ? new window.google.maps.LatLng(this.location_data.Latitude, this.location_data.Longitude)
        : null;
    },
    device_data: {
      // getter
      get: function () {
        return this.$store.state.devices
          ? this.$store.state.devices.filter((elem) => elem.DeviceID == this.device_id)[0]
          : null;
      },
      // setter
      set: function (newValue) {
        let devices = JSON.parse(JSON.stringify(this.$store.state.devices));
        let updated_device_data = { ...newValue };
        let updated_devices_list = devices.map((e) => {
          if (e.DeviceID == this.device_id) {
            e = { ...e, ...updated_device_data };
          }
          return e;
        });
        this.$store.commit("set_devices", updated_devices_list);
      },
    },
    options: function () {
      if (this.current_tab == "Journeys" && !this.current_current_location)
        return this.page_content.maps__journey_config;
      return this.page_content.maps_config;
    },
  },
};
</script>

<style lang="scss" scoped>
.device_details .title {
  font-size: 0.75rem !important;
  font-family: Trade Gothic Regular !important;
}
.border-bottom {
  border-bottom: 1px solid var(--v-restingOutline-base);
}
::v-deep .export-div {
  top: 25px;
  right: 2rem;
}

::v-deep .comment-textarea {
  .v-text-field__details {
    color: transparent !important;
  }
  :disabled {
    color: #000 !important;
  }
}
</style>
